import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import DialogUI from './DialogUI';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { getAttachmentUrl } from '../../utils/fileTabHelper';

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const BUTTON_STEP = 1.25;
const WHEEL_STEP = 1.15;
const DOUBLE_TAP_SCALE = 2.5;
const INITIAL_VIEW = { scale: 1, x: 0, y: 0 };

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const FilePreviewDialog = ({ open, files = [], selectedIndex = 0, handleClose }) => {
  const [index, setIndex] = useState(selectedIndex);
  const [url, setUrl] = useState(null);
  const [view, setView] = useState(INITIAL_VIEW);
  const [dragging, setDragging] = useState(false);
  const [loadedUrl, setLoadedUrl] = useState(null);
  const [errorUrl, setErrorUrl] = useState(null);
  const loaded = Boolean(url) && loadedUrl === url;
  const failed = Boolean(url) && errorUrl === url;

  const viewportRef = useRef(null);
  const imgRef = useRef(null);
  const pointersRef = useRef(new Map());
  const lastPinchDistRef = useRef(null);

  useEffect(() => {
    if (open) setIndex(selectedIndex);
  }, [open, selectedIndex]);

  const file = files[index];

  useEffect(() => {
    setView(INITIAL_VIEW);
  }, [index, open]);

  useEffect(() => {
    if (!open || !file) {
      setUrl(null);
      return;
    }

    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file);
      setUrl(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    }

    setUrl(getAttachmentUrl(file.path));
  }, [open, file]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        setIndex((prev) => Math.max(0, prev - 1));
      }

      if (event.key === 'ArrowRight') {
        setIndex((prev) => Math.min(files.length - 1, prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, files.length]);

  const clampPan = useCallback((x, y, scale) => {
    const viewport = viewportRef.current;
    const img = imgRef.current;
    if (!viewport || !img || scale <= 1) return { x: 0, y: 0 };

    const maxX = Math.max(0, (img.offsetWidth * scale - viewport.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * scale - viewport.clientHeight) / 2);

    return {
      x: clamp(x, -maxX, maxX),
      y: clamp(y, -maxY, maxY),
    };
  }, []);

  const zoomAt = useCallback(
    (factor, cx = 0, cy = 0) => {
      if (!loaded) return;

      setView((prev) => {
        const nextScale = clamp(prev.scale * factor, MIN_SCALE, MAX_SCALE);
        if (nextScale === prev.scale) return prev;
        if (nextScale === MIN_SCALE) return INITIAL_VIEW;

        const ratio = nextScale / prev.scale;
        const pan = clampPan(cx - (cx - prev.x) * ratio, cy - (cy - prev.y) * ratio, nextScale);

        return { scale: nextScale, ...pan };
      });
    },
    [clampPan, loaded]
  );

  const getRelativePoint = (clientX, clientY) => {
    const rect = viewportRef.current.getBoundingClientRect();
    return {
      x: clientX - rect.left - rect.width / 2,
      y: clientY - rect.top - rect.height / 2,
    };
  };

  useEffect(() => {
    const el = viewportRef.current;
    if (!open || !el) return;

    const handleWheel = (event) => {
      event.preventDefault();
      const { x, y } = getRelativePoint(event.clientX, event.clientY);
      zoomAt(event.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP, x, y);
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [open, zoomAt, url]);

  const handlePointerDown = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointersRef.current.size === 1) setDragging(true);
    if (pointersRef.current.size === 2) lastPinchDistRef.current = null;
  };

  const handlePointerMove = (event) => {
    const pointers = pointersRef.current;
    const prevPoint = pointers.get(event.pointerId);
    if (!prevPoint) return;

    const currentPoint = { x: event.clientX, y: event.clientY };
    pointers.set(event.pointerId, currentPoint);

    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);

      if (lastPinchDistRef.current) {
        const center = getRelativePoint((a.x + b.x) / 2, (a.y + b.y) / 2);
        zoomAt(dist / lastPinchDistRef.current, center.x, center.y);
      }
      lastPinchDistRef.current = dist;
      return;
    }

    if (pointers.size === 1) {
      const dx = currentPoint.x - prevPoint.x;
      const dy = currentPoint.y - prevPoint.y;

      setView((prev) => {
        if (prev.scale <= 1) return prev;
        const pan = clampPan(prev.x + dx, prev.y + dy, prev.scale);
        return { ...prev, ...pan };
      });
    }
  };

  const handlePointerUp = (event) => {
    pointersRef.current.delete(event.pointerId);
    lastPinchDistRef.current = null;
    if (pointersRef.current.size === 0) setDragging(false);
  };

  const handleDoubleClick = (event) => {
    if (view.scale > 1) {
      setView(INITIAL_VIEW);
    } else {
      const { x, y } = getRelativePoint(event.clientX, event.clientY);
      zoomAt(DOUBLE_TAP_SCALE, x, y);
    }
  };

  const isZoomed = view.scale > 1;

  return (
    <DialogUI
      open={open}
      onClose={handleClose}
      fullWidth
      headerContent={
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0.5, sm: 2 }}>
          <Typography noWrap sx={{ flex: 1, minWidth: 0 }}>
            {file?.name}
          </Typography>

          <Box display="flex" alignItems="center" flexShrink={0}>
            <IconButton
              size="small"
              color="inherit"
              onClick={() => zoomAt(1 / BUTTON_STEP)}
              disabled={!loaded || view.scale <= MIN_SCALE}
            >
              <ZoomOutIcon />
            </IconButton>

            <Typography fontSize={14} sx={{ minWidth: 44, textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>
              {Math.round(view.scale * 100)}%
            </Typography>

            <IconButton
              size="small"
              color="inherit"
              onClick={() => zoomAt(BUTTON_STEP)}
              disabled={!loaded || view.scale >= MAX_SCALE}
            >
              <ZoomInIcon />
            </IconButton>

            <IconButton size="small" color="inherit" onClick={() => setView(INITIAL_VIEW)} disabled={!isZoomed}>
              <RestartAltIcon />
            </IconButton>
          </Box>

          <Typography fontSize={16} flexShrink={0}>
            {files.length ? index + 1 : 0} / {files.length}
          </Typography>
        </Box>
      }
      closeBtn
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1200px',
            mx: 1,
          },
        },
        '& .MuiDialogContent-root': {
          px: 0.5,
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={{ xs: '8px 0 0', sm: 2 }}
        gap={{ xs: 0, sm: 1 }}
      >
        <IconButton
          disabled={index === 0}
          onClick={() => setIndex((prev) => prev - 1)}
          sx={{ flexShrink: 0, p: { xs: 0.5, sm: 1 } }}
        >
          <ChevronLeftIcon sx={{ fontSize: { xs: 26, sm: 35 } }} />
        </IconButton>

        <Box
          ref={viewportRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={handleDoubleClick}
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            minHeight: loaded ? 0 : 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            touchAction: 'none',
            userSelect: 'none',
            cursor: !loaded ? 'default' : isZoomed ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
          }}
        >
          {!loaded && !failed && <CircularProgress sx={{ position: 'absolute' }} />}
          {failed && (
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <BrokenImageIcon sx={{ fontSize: 56 }} />
              <Typography>A kép nem érhető el</Typography>
            </Box>
          )}

          {url && (
            <Box
              ref={imgRef}
              component="img"
              src={url}
              alt={file?.name || ''}
              draggable={false}
              onLoad={() => setLoadedUrl(url)}
              onError={() => setErrorUrl(url)}
              sx={{
                maxWidth: '100%',
                maxHeight: '75vh',
                minWidth: 0,
                objectFit: 'contain',
                pointerEvents: 'none',
                transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
                transformOrigin: 'center center',
                transition: dragging ? 'none' : 'transform 0.1s ease-out',
                willChange: 'transform',
                opacity: loaded ? 1 : 0,
              }}
            />
          )}
        </Box>
        <IconButton
          disabled={index >= files.length - 1}
          onClick={() => setIndex((prev) => prev + 1)}
          sx={{ flexShrink: 0, p: { xs: 0.5, sm: 1 } }}
        >
          <ChevronRightIcon sx={{ fontSize: { xs: 26, sm: 35 } }} />
        </IconButton>
      </Box>
    </DialogUI>
  );
};

export default FilePreviewDialog;
